// Spell-template preferences that outlive a session: the spell art choices and
// the effects engine's settings. Compass is the durable copy (its relay's
// prefs lane, bridge-prefs.json beside the profiles); the web layout is the
// local cache. On connect the DM Screen pulls Compass's copy and takes it
// when it is newer (or the layout has none); every local change is pushed
// with a stamp, debounced. The newer stamp always wins, so two browsers on
// one Compass converge and a browser without Compass keeps working.
import { newRequestId, onBridge, request } from "../bridge";
import type { Layout } from "./types";

export const PREF_KEY = "dmScreen.effects";
/** The layout fields that make up the spell-template preferences. */
export const PREF_FIELDS = ["spellArt", "spellArtExtra", "spellArtAll", "effectsEngine", "effectsAuto", "effectsRollOnCast", "effectsAutoPlace", "effectsAutoPlaceSingle", "effectDcDefault", "tokenProneMarker", "tokenFollow"] as const;
export const STAMP_FIELD = "effectPrefsAt";

export type PrefValues = Partial<Record<(typeof PREF_FIELDS)[number], unknown>>;
export const prefValues = (layout: Layout): PrefValues => { const out: PrefValues = {}; for (const k of PREF_FIELDS) if (layout[k] !== undefined) out[k] = layout[k]; return out; };
export const prefStamp = (layout: Layout) => Number(layout[STAMP_FIELD]) || 0;
export const samePrefs = (a: PrefValues, b: PrefValues) => JSON.stringify(a) === JSON.stringify(b);

interface Remote { ok: boolean; error?: string; offline?: boolean; value?: { values?: PrefValues } | null; at?: string | number | null; stored?: boolean }
const op = (o: Record<string, unknown>) => request<Remote>({ type: "DM_SCREEN_LMC_PREFS_REQUEST", requestId: newRequestId("prefs"), op: o }, { resultType: "DM_SCREEN_LMC_PREFS_RESULT", timeoutMs: 12000 }).catch((e) => ({ ok: false, error: e?.message || String(e) } as Remote));

/** Compass's copy: the document, "empty" when Compass has none yet, "offline" when no
 *  Compass answers, "error" when the lane failed (never mistaken for empty: a stale local
 *  copy must not be pushed over a newer one because of a timeout). */
export const pullPrefs = async (): Promise<{ values: PrefValues; at: number } | "empty" | "offline" | "error"> => {
  const r = await op({ op: "get", key: PREF_KEY });
  if (!r.ok) return r.offline ? "offline" : "error";
  const values = r.value && typeof r.value === "object" && r.value.values && typeof r.value.values === "object" ? r.value.values : null;
  return values ? { values, at: Number(r.at) || 0 } : "empty";
};
export const pushPrefs = async (values: PrefValues, at: number) => op({ op: "set", key: PREF_KEY, value: { values, savedBy: "dm-screen" }, at: String(at) });

/** Compass's copy applied over the layout, stamped. */
export function applyPrefs(layout: Layout, values: PrefValues, at: number): Layout {
  const next: Layout = { ...layout, [STAMP_FIELD]: at };
  for (const k of PREF_FIELDS) { if (values[k] !== undefined) (next as Record<string, unknown>)[k] = values[k]; }
  return next;
}

// the lane's readiness (the page half announces it on load and on every ping)
let ready = false; const subs = new Set<() => void>(); let watching = false;
export const subscribePrefsLane = (fn: () => void) => { if (!watching) { watching = true; onBridge((m) => { if (m.type === "DM_SCREEN_LMC_PREFS_READY") { const was = ready; ready = Boolean((m.capabilities as Record<string, boolean> | undefined)?.lmcPrefs); if (was !== ready) subs.forEach((s) => s()); } }); } subs.add(fn); return () => { subs.delete(fn); }; };
export const prefsLaneReady = () => ready;
